// @ts-check

import { countryByIso } from './countries';
import { cleanMeasurements, validateMeasurements } from './customerMeasurements';
import { formatDay } from './salesEvents';

/** Pure helpers behind the in-person sale form (PosOrderModal): payload, validation, WhatsApp text. */

export const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi_qr', label: 'UPI QR' },
  { value: 'razorpay_link', label: 'Payment link' },
];

/** A partial payment line always represents money already confirmed in hand by staff - never a
 * pending gateway link, which only resolves once the existing payment-link flow's own webhook
 * marks the whole order paid. Mirrors backend/utils/order_payments.LEDGER_PAYMENT_METHODS. */
export const LEDGER_PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi_qr', label: 'UPI QR' },
  { value: 'bank_transfer', label: 'Bank transfer' },
];

/** A discount agreed on the spot at the stand - not a promo code (no usage limit, no per-customer
 * tracking, no DB row). Same vocabulary as the online DiscountCode system, for consistency. */
export const DISCOUNT_TYPES = [
  { value: 'percentage', label: '%' },
  { value: 'fixed_amount', label: '₹' },
];

/** Fulfilment and payment-completeness were previously two separate controls (a checkbox and a
 * button toggle) - easy to combine wrong, e.g. forgetting to check "ship to customer" on a
 * partial-payment sale, which used to write a placeholder claiming the piece was already handed
 * over. One dropdown of the real scenario makes that combination impossible to get wrong: a
 * piece is never handed over before it's fully paid, so that rule is baked into the option
 * itself instead of being two independently-forgettable steps. */
export const ORDER_SCENARIOS = [
  { value: 'handover_full', shipToCustomer: false, paymentMode: 'full', label: 'Customer takes it today — paid in full' },
  {
    value: 'handover_partial', shipToCustomer: false, paymentMode: 'partial',
    label: 'Customer takes it today — partial payment (rest due before handover)',
  },
  { value: 'ship_full', shipToCustomer: true, paymentMode: 'full', label: 'Shipping to customer — paid in full' },
  {
    value: 'ship_partial', shipToCustomer: true, paymentMode: 'partial',
    label: 'Shipping to customer — partial payment (rest due before dispatch)',
  },
];

/** The dropdown's current value, derived from the form's own shipToCustomer/paymentMode fields
 * (the source of truth stays those two fields - the dropdown is just a combined view over them). */
export function scenarioValue(shipToCustomer, paymentMode) {
  const match = ORDER_SCENARIOS.find((s) => s.shipToCustomer === shipToCustomer && s.paymentMode === paymentMode);
  return match ? match.value : ORDER_SCENARIOS[0].value;
}

const DEEP_DISCOUNT_RATIO = 0.5;

const inr = (amount) => `₹${Number(amount).toLocaleString('en-IN')}`;
const digits = (text) => String(text ?? '').replace(/\D/g, '');

/** @param {string | null | undefined} value */
const clean = (value) => {
  const text = (value ?? '').trim();
  return text === '' ? null : text;
};

/** Dial code (e.g. "+91") of the country picked beside the phone number; India's when none is picked. */
export const phoneDial = (form) => countryByIso(form.phoneCountry)?.dial ?? '+91';

/**
 * What checkout charges for this piece: the sale price when there is one, else the list price.
 * @param {{price?: number, sale_price?: number | null}} variant
 */
export const listPrice = (variant) => Number(variant.sale_price || variant.price || 0);

/** @param {Array<{quantity: number, unitPrice: number}>} lines */
export const orderTotal = (lines) => lines.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0);

/** True when a price is half the list price or less, which is more likely a typo than a discount. */
export const isDeepDiscount = (unitPrice, list) => list > 0 && unitPrice <= list * DEEP_DISCOUNT_RATIO;

/**
 * Whole-sale discount amount, never negative, never more than the subtotal. Mirrors
 * backend/utils/pos_discount.py exactly - the server recomputes this independently rather than
 * trusting the client's number, so this copy is for the live on-screen total only.
 * @param {number} subtotal
 * @param {string | null} discountType 'percentage' | 'fixed_amount'
 * @param {number} discountValue
 */
export function discountAmount(subtotal, discountType, discountValue) {
  if (!discountType || !(discountValue > 0)) return 0;
  const raw = discountType === 'percentage' ? (subtotal * discountValue) / 100 : discountValue;
  return Math.min(Math.max(raw, 0), subtotal);
}

/**
 * Body for POST /api/admin/pos-orders.
 * @param {{form: any, lines: Array<{variant: any, quantity: number, unitPrice: number, measurements?: Record<string, string>, fitNotes?: string}>, requestId: string}} args
 */
export function buildPosPayload({ form, lines, requestId }) {
  const ship = form.shipToCustomer;
  const isPartial = form.paymentMode === 'partial';
  const isLink = !isPartial && form.paymentMethod === 'razorpay_link';
  return {
    request_id: requestId,
    // Omitted (not tied to any pop-up event) for a general admin-entered order - see
    // PosOrderModal's requireEvent prop.
    event_id: form.eventId || null,
    customer_first_name: form.firstName.trim(),
    customer_last_name: clean(form.lastName),
    customer_phone: form.phone.trim(),
    customer_phone_country_code: phoneDial(form),
    customer_email: clean(form.email),
    items: lines.map((l) => ({
      product_id: l.variant.product_id,
      variant_id: l.variant.variant_id,
      size: l.variant.size,
      quantity: l.quantity,
      unit_price: l.unitPrice,
      measurements: cleanMeasurements(l.measurements),
      fit_notes: clean(l.fitNotes),
    })),
    payment_method: isPartial ? null : form.paymentMethod,
    payment_reference: !isPartial && form.paymentMethod === 'upi_qr' ? clean(form.paymentReference) : null,
    payment_lines: isPartial
      ? form.paymentLines.map((l) => ({ amount: l.amount, method: l.method, reference: clean(l.reference) }))
      : null,
    ship_to_customer: ship,
    shipping_address_line1: ship ? clean(form.shipping_address_line1) : null,
    shipping_address_line2: ship ? clean(form.shipping_address_line2) : null,
    shipping_city: ship ? clean(form.shipping_city) : null,
    shipping_state: ship ? clean(form.shipping_state) : null,
    shipping_postal_code: ship ? clean(form.shipping_postal_code) : null,
    shipping_country: form.shipping_country.trim() || 'India',
    promised_dispatch_date: ship && form.dispatchDate ? form.dispatchDate : null,
    // A payment link sale is always "now"; only cash / UPI QR sales can be back-dated.
    sold_at: form.soldAt && !isLink ? new Date(form.soldAt).toISOString() : null,
    admin_notes: clean(form.notes),
    discount_type: form.discountValue > 0 ? form.discountType : null,
    discount_value: form.discountValue > 0 ? form.discountValue : null,
    discount_reason: form.discountValue > 0 ? clean(form.discountReason) : null,
  };
}

/**
 * First problem with the sale, or null when it is ready to submit.
 * @param {any} form
 * @param {Array<{quantity: number, unitPrice: number, variant?: any, product?: {name: string}, measurements?: Record<string, string>}>} lines
 * @param {{requireEvent?: boolean}} [options] requireEvent is false for a general order not tied to any pop-up event
 * @returns {string | null}
 */
export function validatePosForm(form, lines, { requireEvent = true } = {}) {
  if (requireEvent && !form.eventId) return 'Choose the event';
  if (lines.length === 0) return 'Add at least one piece';
  const unsettled = lines.find((l) => !l.variant);
  if (unsettled) return `Choose the size and color for ${unsettled.product?.name ?? 'each piece'}`;
  if (lines.some((l) => !(l.unitPrice > 0))) return 'Every piece needs a price above zero';
  for (const l of lines) {
    const problem = validateMeasurements(l.measurements);
    if (problem) return `${l.product?.name ?? l.variant?.product_name}: ${problem}`;
  }
  if (!form.firstName.trim()) return "Enter the customer's name";
  if (digits(form.phone).length < 7) return 'Enter a phone number (at least 7 digits) for the WhatsApp follow-up';
  if (
    form.shipToCustomer &&
    !(
      form.shipping_address_line1.trim() &&
      form.shipping_city.trim() &&
      form.shipping_state.trim() &&
      form.shipping_postal_code.trim()
    )
  ) {
    return 'Enter the full shipping address (street, city, state, postal code)';
  }
  if (form.discountValue < 0) return 'Discount cannot be negative';
  if (form.discountType === 'percentage' && form.discountValue > 100)
    return 'A percentage discount cannot be more than 100%';
  if (form.paymentMode === 'partial') {
    if (form.paymentLines.length === 0) return 'Add at least one payment';
    if (form.paymentLines.some((l) => !(l.amount > 0))) return 'Every payment needs an amount above zero';
    const subtotal = orderTotal(lines);
    const total = subtotal - discountAmount(subtotal, form.discountType, form.discountValue);
    const linesTotal = form.paymentLines.reduce((sum, l) => sum + l.amount, 0);
    if (linesTotal > total) return 'The payments add up to more than the order total';
  }
  return null;
}

/** wa.me link that opens WhatsApp to this number with the message prefilled. */
export function whatsappUrl(countryCode, phone, message) {
  const cc = digits(countryCode);
  const national = digits(phone).replace(/^0+/, '');
  // The phone may already carry the country code (typed as +91 ...).
  const full = national.length > 10 && national.startsWith(cc) ? national : cc + national;
  return `https://wa.me/${full}?text=${encodeURIComponent(message)}`;
}

/** Full URL the customer opens to pay: storefront origin plus the path the API returned. */
export function paymentUrl(siteUrl, payPath) {
  if (!siteUrl || !payPath) return '';
  return `${siteUrl.replace(/\/+$/, '')}${payPath}`;
}

export function paymentLinkMessage({ name, total, url }) {
  return `Hi ${name}, here is your secure KAAY payment link for ${inr(total)}:\n${url}`;
}

/** @param {{name: string, orderNumber: string, lines: any[], total: number, methodLabel: string, shipToCustomer: boolean, dispatchDate?: string}} args */
export function receiptMessage({ name, orderNumber, lines, total, methodLabel, shipToCustomer, dispatchDate }) {
  const pieces = lines
    .map(
      (l) =>
        `${l.quantity} x ${l.variant.product_name} (${[l.variant.size, l.variant.color].filter(Boolean).join(', ')})`,
    )
    .join('\n');
  return [
    `Hi ${name}, thank you for shopping with KAAY.`,
    `Order ${orderNumber}`,
    pieces,
    `Total: ${inr(total)}, paid by ${methodLabel}.`,
    shipToCustomer ? 'We will ship your order and share the tracking details on WhatsApp.' : '',
    shipToCustomer && dispatchDate ? `Expected dispatch: ${formatDay(dispatchDate)}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

/** @param {{name: string, orderNumber: string, lines: any[], amountPaid: number, balanceDue: number, dispatchDate?: string}} args */
export function advanceReceiptMessage({ name, orderNumber, lines, amountPaid, balanceDue, dispatchDate }) {
  const pieces = lines
    .map(
      (l) =>
        `${l.quantity} x ${l.variant.product_name} (${[l.variant.size, l.variant.color].filter(Boolean).join(', ')})`,
    )
    .join('\n');
  return [
    `Hi ${name}, thank you for shopping with KAAY.`,
    `Order ${orderNumber}`,
    pieces,
    `Advance received: ${inr(amountPaid)}. Balance due: ${inr(balanceDue)}.`,
    dispatchDate ? `Expected dispatch once complete: ${formatDay(dispatchDate)}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}
